import { desktopBridge } from '../../services/desktopBridge';
import type { ElectronAPI } from '../../types/electron';

/**
 * Splits a file path into its name and directory path components
 * @param fileName - Full file path (e.g., "folder/subfolder/file.txt")
 * @returns Object with `name` (filename) and `path` (directory path)
 */
export function getFileNameParts(fileName: string): {
  name: string;
  path: string;
} {
  const parts = fileName.split('/');
  const name = parts.pop() || '';
  const path = parts.join('/');
  return { name, path };
}

/**
 * Remove the project folder path from a file path
 * @param relativePath - The full relative path of the file
 * @param projectFolderPath - The project folder path to remove
 * @param fileName - The file name as fallback
 * @returns The file path relative to the project folder
 */
export function removeProjectPathFromFilePath(
  relativePath: string,
  projectFolderPath: string,
  fileName: string
): string {
  // If the file path starts with the project folder path, remove it
  if (relativePath.startsWith(projectFolderPath + '/')) {
    return relativePath.substring(projectFolderPath.length + 1);
  } else if (relativePath === projectFolderPath) {
    // Handle case where the file is directly in the project folder
    return fileName;
  }
  // If no match, return the original relative path
  return relativePath;
}

/**
 * Navigate a FileSystemDirectoryHandle by path parts
 * @param rootHandle - The root directory handle to start from
 * @param pathParts - Array of path segments to navigate through
 * @param fallbackName - Optional fallback name if path navigation fails
 * @returns The directory handle at the end of the path, or null if navigation fails
 */
export async function navigateDirectoryHandleByPath(
  rootHandle: FileSystemDirectoryHandle,
  pathParts: string[],
  fallbackName?: string
): Promise<FileSystemDirectoryHandle | null> {
  try {
    let currentHandle = rootHandle;

    // Navigate through the path parts
    for (const part of pathParts) {
      if (part) {
        currentHandle = await currentHandle.getDirectoryHandle(part, {
          create: false,
        });
      }
    }
    return currentHandle;
  } catch (error) {
    // If we can't navigate by path, try by fallback name if provided
    if (fallbackName) {
      try {
        return await rootHandle.getDirectoryHandle(fallbackName, {
          create: false,
        });
      } catch {
        // If we still can't find it, use the root handle as fallback
        // This might happen if the project is at the root level
        return rootHandle;
      }
    }
    return null;
  }
}

/**
 * Gets file modification time from the file system, with fallback to provided date
 * Handles both Electron and desktop bridge environments
 * @param filePath - Full path to the file
 * @param fallbackDate - ISO date string to use if file system access fails
 * @param electronAPI - Optional Electron API instance (if in Electron environment)
 * @param isElectron - Whether running in Electron environment
 * @returns Promise resolving to modification time as number (timestamp) or ISO string
 */
export async function getFileModificationTime(
  filePath: string,
  fallbackDate: string,
  electronAPI?: ElectronAPI | null,
  isElectron?: boolean
): Promise<number | string> {
  try {
    let fileInfo;

    // Try Electron API first if available
    if (isElectron && electronAPI?.getFileInfo) {
      fileInfo = await electronAPI.getFileInfo(filePath);
    } else if (
      isElectron &&
      typeof window !== 'undefined' &&
      window.electronAPI?.getFileInfo
    ) {
      // Fallback to window.electronAPI if not passed as parameter
      fileInfo = await window.electronAPI.getFileInfo(filePath);
    } else {
      // Try desktop bridge for web version
      const isDesktopAvailable = await desktopBridge.isDesktopAvailable();
      if (isDesktopAvailable) {
        fileInfo = await desktopBridge.getFileInfo(filePath);
      } else {
        // Fallback to provided date if we can't access file system
        return typeof fallbackDate === 'string'
          ? new Date(fallbackDate).getTime()
          : fallbackDate;
      }
    }

    if (fileInfo?.success && fileInfo.info?.modified) {
      const modified = fileInfo.info.modified;
      // Return as timestamp (number) for consistency
      return modified instanceof Date
        ? modified.getTime()
        : new Date(modified).getTime();
    } else {
      // Fallback to provided date if file info is not available
      return typeof fallbackDate === 'string'
        ? new Date(fallbackDate).getTime()
        : fallbackDate;
    }
  } catch (error) {
    // Fallback to provided date if there's an error getting file info
    console.warn(
      `Failed to get file info for ${filePath}, using fallback date:`,
      error
    );
    return typeof fallbackDate === 'string'
      ? new Date(fallbackDate).getTime()
      : fallbackDate;
  }
}

/**
 * Gets file modification time as ISO string (for display purposes)
 * @param filePath - Full path to the file
 * @param fallbackDate - ISO date string to use if file system access fails
 * @param electronAPI - Optional Electron API instance (if in Electron environment)
 * @param isElectron - Whether running in Electron environment
 * @returns Promise resolving to modification time as ISO string
 */
export async function getFileModificationTimeISO(
  filePath: string,
  fallbackDate: string,
  electronAPI?: ElectronAPI | null,
  isElectron?: boolean
): Promise<string> {
  const modTime = await getFileModificationTime(
    filePath,
    fallbackDate,
    electronAPI,
    isElectron
  );

  // Convert to ISO string if it's a number (timestamp)
  if (typeof modTime === 'number') {
    return new Date(modTime).toISOString();
  }

  // If it's already a string, return it as-is (should already be ISO format)
  return typeof modTime === 'string'
    ? modTime
    : new Date(modTime).toISOString();
}
